import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  Chip,
  Stack,
  Switch,
  FormControlLabel,
  LinearProgress,
} from '@mui/material';
import {
  ContentCopy as CopyIcon,
  Refresh as RefreshIcon,
  Send as SendIcon,
  LinkedIn,
  Twitter,
  Facebook,
  Instagram,
  Share as ShareIcon,
  History as HistoryIcon
} from '@mui/icons-material';
import { useAuthenticatedFetch } from '../../hooks/useAuthenticatedFetch';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

interface SocialMediaContentGeneratorProps {
  marketId: string;
  marketData: any;
}

interface GenerationRequest {
  platform: string;
  postLength: string;
  tone: string;
  language: string;
  topicFocus: string;
  includeHashtags: boolean;
  includeEmojis: boolean;
  modelId: number | undefined;
}

interface GeneratedContent {
  id: number;
  generatedContent: string;
  marketUrl: string;
  platform: string;
  postLength: string;
  characterCount: number;
  createdAt: string;
  llmProvider: string;
  llmModel: string;
  generationDurationMs: number;
}

interface OperationStatus {
  id: string;
  operation_type: string;
  status: 'started' | 'in_progress' | 'completed' | 'error';
  progress_text?: string | null;
  result?: any;
  error_message?: string | null;
}

interface LlmModel {
  id: number;
  modelName: string;
  provider: string;
  family: string | null;
  alias: string | null;
  release: string;
  isActive: boolean;
}

export const SocialMediaContentGenerator: React.FC<SocialMediaContentGeneratorProps> = ({
  marketId,
  marketData
}) => {
  const { authenticatedFetch } = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  // Form state
  const [request, setRequest] = useState<GenerationRequest>({
    platform: 'linkedin',
    postLength: 'medium',
    tone: 'professional',
    language: 'en',
    topicFocus: '',
    includeHashtags: true,
    includeEmojis: false,
    modelId: undefined // Will be set to first available model once loaded
  });

  // UI state
  const [generatedContent, setGeneratedContent] = useState<GeneratedContent | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const [operationId, setOperationId] = useState<string | null>(null);
  const [operationStatus, setOperationStatus] = useState<OperationStatus | null>(null);
  const [pollingError, setPollingError] = useState<string | null>(null);

  // Mutation for starting content generation
  const generateMutation = useMutation({
    mutationFn: async (req: GenerationRequest) => {
      const response = await authenticatedFetch(
        `/api/marketcompass/admin/markets/${marketId}/social/generate`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(req)
        }
      );

      if (!response) {
        throw new Error('Failed to start generation: No response received');
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to start generation');
      }

      return response.json();
    },
    onSuccess: (data) => {
      if (data.operationId) {
        setOperationId(data.operationId);
        setPollingError(null);
      }
    }
  });

  // Poll for operation status
  useEffect(() => {
    if (!operationId) return;

    let intervalId: NodeJS.Timeout;

    const pollStatus = async () => {
      try {
        const response = await authenticatedFetch(
          `/api/operations/${operationId}`
        );

        if (!response) {
          setPollingError('Failed to check status: No response received');
          return;
        }

        if (!response.ok) {
          const errorData = await response.json();
          setPollingError(errorData.error || 'Failed to check operation status');
          return;
        }

        const data = await response.json();
        const status: OperationStatus = data.operation;
        setOperationStatus(status);

        if (status.status === 'completed') {
          // Extract the generated content from the result
          setGeneratedContent(status.result);
          setOperationId(null);
          setOperationStatus(null);
          queryClient.invalidateQueries({ queryKey: ['social-history', marketId] });
        } else if (status.status === 'error') {
          setPollingError(status.error_message || 'Generation failed');
          setOperationId(null);
          setOperationStatus(null);
        }
      } catch (error) {
        setPollingError(error instanceof Error ? error.message : 'Unknown error while polling');
      }
    };

    // Poll immediately, then every 2 seconds
    pollStatus();
    intervalId = setInterval(pollStatus, 2000);

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [operationId, authenticatedFetch, queryClient, marketId]);

  // Query for generation history
  const { data: historyData, isLoading: historyLoading } = useQuery({
    queryKey: ['social-history', marketId],
    queryFn: async () => {
      const response = await authenticatedFetch(
        `/api/marketcompass/admin/markets/${marketId}/social/history?limit=10`
      );

      if (!response) {
        throw new Error('Failed to fetch history: No response received');
      }

      if (!response.ok) {
        throw new Error('Failed to fetch generation history');
      }

      return response.json();
    },
    enabled: showHistory
  });

  // Query for available LLM models
  const { data: modelsData, isLoading: modelsLoading } = useQuery({
    queryKey: ['llm-models'],
    queryFn: async () => {
      const response = await authenticatedFetch('/api/marketcompass/admin/models');
      if (!response || !response.ok) {
        throw new Error('Failed to fetch models');
      }
      const result = await response.json();
      return result.data;
    }
  });

  const models = modelsData?.models || [];
  const groupedModels = modelsData?.grouped || {};
  const availableModels = Array.isArray(models) ? models.filter((m: LlmModel) => m.isActive) : [];

  // Set default model when models are loaded
  useEffect(() => {
    if (availableModels.length > 0 && !request.modelId) {
      setRequest(prev => ({ ...prev, modelId: availableModels[0].id }));
    }
  }, [availableModels, request.modelId]);

  const handleGenerate = () => {
    setGeneratedContent(null);
    setPollingError(null);
    generateMutation.mutate(request);
  };

  const handleCopyToClipboard = async () => {
    if (generatedContent) {
      await navigator.clipboard.writeText(generatedContent.generatedContent);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    }
  };

  const getPlatformIcon = (platform: string) => {
    switch (platform) {
      case 'linkedin': return <LinkedIn />;
      case 'twitter': return <Twitter />;
      case 'facebook': return <Facebook />;
      case 'instagram': return <Instagram />;
      default: return <ShareIcon />;
    }
  };

  const getCharacterLimit = (length: string): number => {
    switch (length) {
      case 'short': return 280;
      case 'medium': return 600;
      case 'long': return 1300;
      default: return 600;
    }
  };

  const characterLimit = getCharacterLimit(request.postLength);
  const characterCount = generatedContent?.characterCount || 0;
  const isOverLimit = characterCount > characterLimit;
  const isGenerating = generateMutation.isPending || operationId !== null;
  const canGenerate = request.modelId !== undefined && !isGenerating;

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Social Media Content Generator
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Generate AI-powered social media posts about {marketData?.market_name || 'this market'} using the latest market report.
      </Typography>

      <Grid container spacing={3} sx={{ mt: 2 }}>
        {/* Configuration Panel */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Content Settings
            </Typography>

            <Stack spacing={3}>
              {/* Platform Selection */}
              <FormControl fullWidth>
                <InputLabel>Platform</InputLabel>
                <Select
                  value={request.platform}
                  label="Platform"
                  onChange={(e) => setRequest({ ...request, platform: e.target.value })}
                  disabled={isGenerating}
                >
                  <MenuItem value="linkedin">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LinkedIn fontSize="small" />
                      LinkedIn
                    </Box>
                  </MenuItem>
                  <MenuItem value="twitter">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Twitter fontSize="small" />
                      Twitter/X
                    </Box>
                  </MenuItem>
                  <MenuItem value="facebook">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Facebook fontSize="small" />
                      Facebook
                    </Box>
                  </MenuItem>
                  <MenuItem value="instagram">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Instagram fontSize="small" />
                      Instagram
                    </Box>
                  </MenuItem>
                  <MenuItem value="general">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <ShareIcon fontSize="small" />
                      General
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>

              {/* Post Length */}
              <FormControl fullWidth>
                <InputLabel>Post Length</InputLabel>
                <Select
                  value={request.postLength}
                  label="Post Length"
                  onChange={(e) => setRequest({ ...request, postLength: e.target.value })}
                  disabled={isGenerating}
                >
                  <MenuItem value="short">Short (100-280 chars)</MenuItem>
                  <MenuItem value="medium">Medium (300-600 chars)</MenuItem>
                  <MenuItem value="long">Long (600-1300 chars)</MenuItem>
                </Select>
              </FormControl>

              {/* Tone */}
              <FormControl fullWidth>
                <InputLabel>Tone</InputLabel>
                <Select
                  value={request.tone}
                  label="Tone"
                  onChange={(e) => setRequest({ ...request, tone: e.target.value })}
                  disabled={isGenerating}
                >
                  <MenuItem value="professional">Professional</MenuItem>
                  <MenuItem value="casual">Casual</MenuItem>
                  <MenuItem value="enthusiastic">Enthusiastic</MenuItem>
                  <MenuItem value="analytical">Analytical</MenuItem>
                  <MenuItem value="conversational">Conversational</MenuItem>
                </Select>
              </FormControl>

              {/* Language */}
              <FormControl fullWidth>
                <InputLabel>Language</InputLabel>
                <Select
                  value={request.language}
                  label="Language"
                  onChange={(e) => setRequest({ ...request, language: e.target.value })}
                  disabled={isGenerating}
                >
                  <MenuItem value="en">English</MenuItem>
                  <MenuItem value="es">Spanish</MenuItem>
                  <MenuItem value="fr">French</MenuItem>
                  <MenuItem value="de">German</MenuItem>
                  <MenuItem value="it">Italian</MenuItem>
                  <MenuItem value="pt">Portuguese</MenuItem>
                  <MenuItem value="zh">Chinese</MenuItem>
                  <MenuItem value="ja">Japanese</MenuItem>
                </Select>
              </FormControl>

              {/* Model Selection */}
              <FormControl fullWidth required>
                <InputLabel>AI Model</InputLabel>
                <Select
                  value={request.modelId || ''}
                  label="AI Model"
                  onChange={(e) => setRequest({ ...request, modelId: Number(e.target.value) })}
                  disabled={isGenerating || modelsLoading}
                  required
                >
                  {modelsLoading ? (
                    <MenuItem value="">Loading models...</MenuItem>
                  ) : (
                    Object.entries(groupedModels).map(([provider, families]) => [
                      <MenuItem key={`header-${provider}`} disabled>
                        <Typography variant="overline" sx={{ color: 'text.primary', fontWeight: 600 }}>{provider}</Typography>
                      </MenuItem>,
                      ...Object.entries(families as Record<string, LlmModel[]>).map(([family, familyModels]) =>
                        familyModels.map((model) => (
                          <MenuItem key={model.id} value={model.id} sx={{ pl: 3 }}>
                            <Typography variant="body2">{model.modelName}</Typography>
                          </MenuItem>
                        ))
                      )
                    ])
                  )}
                </Select>
              </FormControl>

              {/* Topic Focus */}
              <TextField
                fullWidth
                label="Topic Focus (Optional)"
                value={request.topicFocus}
                onChange={(e) => setRequest({ ...request, topicFocus: e.target.value })}
                placeholder="e.g., RevPAR growth, occupancy trends, luxury segment"
                multiline
                rows={2}
                helperText="Specify a particular aspect to emphasize in the post"
                inputProps={{ maxLength: 500 }}
                disabled={isGenerating}
              />

              {/* Additional Options */}
              <Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={request.includeHashtags}
                      onChange={(e) => setRequest({ ...request, includeHashtags: e.target.checked })}
                      disabled={isGenerating}
                    />
                  }
                  label="Include Hashtags"
                />
              </Box>

              {/* Generate Button */}
              <Button
                variant="contained"
                size="large"
                startIcon={isGenerating ? <CircularProgress size={20} /> : <SendIcon />}
                onClick={handleGenerate}
                disabled={!canGenerate}
                fullWidth
              >
                {isGenerating ? 'Generating...' : 'Generate Content'}
              </Button>

              {/* Progress Display */}
              {operationStatus && (
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {operationStatus.progress_text || 'Processing...'}
                  </Typography>
                  <LinearProgress />
                </Box>
              )}

              {/* Error Display */}
              {(generateMutation.isError || pollingError) && (
                <Alert severity="error">
                  {pollingError || (generateMutation.error as Error).message}
                </Alert>
              )}
            </Stack>
          </Paper>
        </Grid>

        {/* Generated Content Panel */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, minHeight: 400 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Generated Content</Typography>
              {generatedContent && (
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Tooltip title={copySuccess ? 'Copied!' : 'Copy to clipboard'}>
                    <IconButton onClick={handleCopyToClipboard} color={copySuccess ? 'success' : 'default'}>
                      <CopyIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Generate new variation">
                    <IconButton onClick={handleGenerate} disabled={isGenerating}>
                      <RefreshIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              )}
            </Box>

            {!generatedContent && !isGenerating && (
              <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: 300,
                color: 'text.secondary'
              }}>
                <ShareIcon sx={{ fontSize: 60, mb: 2, opacity: 0.3 }} />
                <Typography variant="body1">
                  Configure your content settings and click "Generate Content" to create a social media post.
                </Typography>
              </Box>
            )}

            {generatedContent && (
              <Box>
                {/* Metadata Chips */}
                <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
                  <Chip
                    icon={getPlatformIcon(generatedContent.platform)}
                    label={generatedContent.platform}
                    size="small"
                    color="primary"
                  />
                  <Chip
                    label={`${generatedContent.characterCount} chars`}
                    size="small"
                    color={isOverLimit ? 'error' : 'success'}
                  />
                  <Chip
                    label={generatedContent.llmProvider}
                    size="small"
                    variant="outlined"
                  />
                  <Chip
                    label={`${(generatedContent.generationDurationMs / 1000).toFixed(1)}s`}
                    size="small"
                    variant="outlined"
                  />
                </Stack>

                {/* Character Count Warning */}
                {isOverLimit && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    Content exceeds recommended {characterLimit} character limit for {request.postLength} posts.
                    Consider regenerating with a shorter length.
                  </Alert>
                )}

                {/* Generated Text */}
                <Card variant="outlined" sx={{ mb: 2 }}>
                  <CardContent>
                    <Typography
                      variant="body1"
                      sx={{
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        fontFamily: 'inherit'
                      }}
                    >
                      {generatedContent.generatedContent}
                    </Typography>
                  </CardContent>
                </Card>

                {/* Market URL */}
                <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    Market URL included in post:
                  </Typography>
                  <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                    {generatedContent.marketUrl}
                  </Typography>
                </Box>
              </Box>
            )}
          </Paper>

          {/* Generation History Toggle */}
          <Box sx={{ mt: 2 }}>
            <Button
              startIcon={<HistoryIcon />}
              onClick={() => setShowHistory(!showHistory)}
              variant="outlined"
              fullWidth
            >
              {showHistory ? 'Hide' : 'Show'} Generation History
            </Button>
          </Box>

          {/* History Panel */}
          {showHistory && (
            <Paper sx={{ p: 3, mt: 2 }}>
              <Typography variant="h6" gutterBottom>
                Recent Generations
              </Typography>
              {historyLoading && <CircularProgress />}
              {historyData?.data?.generations?.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  No previous generations found.
                </Typography>
              )}
              {historyData?.data?.generations?.map((gen: any) => (
                <Card key={gen.id} variant="outlined" sx={{ mb: 2 }}>
                  <CardContent>
                    <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap: 'wrap' }}>
                      <Chip icon={getPlatformIcon(gen.platform)} label={gen.platform} size="small" />
                      <Chip label={gen.tone} size="small" variant="outlined" />
                      <Chip label={gen.language} size="small" variant="outlined" />
                    </Stack>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mb: 1 }}>
                      {gen.generatedContent.substring(0, 200)}
                      {gen.generatedContent.length > 200 ? '...' : ''}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {new Date(gen.createdAt).toLocaleString()} • {gen.createdBy}
                    </Typography>
                  </CardContent>
                </Card>
              ))}
            </Paper>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};
